import { unwrap, type Schemas } from '@mrigl/api-client';
import type { MessageKey } from '@mrigl/i18n';
import { isGovernorateCode } from '@mrigl/shared';
import {
  Alert,
  Button,
  Card,
  CheckboxGroup,
  SelectField,
  TextAreaField,
  TextField,
} from '@mrigl/ui';
import { Check, Circle } from 'lucide-react';
import { useCallback, useEffect, useId, useState, type SubmitEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../api.js';
import { Loading } from '../../components.js';
import { useI18n } from '../../i18n.js';
import { PaymentInstructions, planName } from '../billing/BillingPage.js';
import { governorateOptions } from '../checkout/shared.js';
import { display, newKey, toMinor, useStore } from '../catalog/shared.js';
import { uploadImage } from '../MediaPage.js';
import { applySignupPlan } from './signup-plan.js';

type Onboarding = Schemas['OnboardingState'];
type StoreDetails = Schemas['StoreDetails'];
type StepKey = Onboarding['steps'][number]['key'];
type Screen = StepKey | 'publish';

const SCREENS: readonly Screen[] = ['plan', 'contact', 'legal', 'delivery', 'products', 'publish'];

function firstOpen(state: Onboarding): Screen {
  return state.steps.find((step) => !step.done)?.key ?? 'publish';
}

/**
 * The merchant onboarding wizard (ADR 0026): plan, contact, legal details, delivery and
 * payment, first products, then publication. Progress comes from the store's data, so the
 * merchant can leave and come back; optional steps (operator settings) can be skipped.
 */
export function SetupWizardPage() {
  const { storeId = '' } = useParams();
  const { t, locale } = useI18n();
  const { store: loaded, error: storeError } = useStore(storeId, t);
  const [store, setStore] = useState<StoreDetails | null>(null);
  const [state, setState] = useState<Onboarding | null>(null);
  const [screen, setScreen] = useState<Screen | null>(null);
  const [error, setError] = useState<string | null>(null);
  const headingId = useId();

  useEffect(() => {
    if (loaded !== null) setStore(loaded);
  }, [loaded]);

  const refresh = useCallback(async () => {
    try {
      const next = await unwrap(
        api.GET('/admin-api/v1/stores/{storeId}/onboarding', { params: { path: { storeId } } }),
      );
      setState(next);
      return next;
    } catch (failure) {
      setError(errorMessage(t, failure));
      return null;
    }
  }, [storeId, t]);

  useEffect(() => {
    // A plan clicked on the landing page is chosen first (ADR 0027).
    void applySignupPlan(storeId)
      .then(() => refresh())
      .then((next) => {
        if (next !== null) setScreen(next.publishedAt === null ? firstOpen(next) : 'publish');
      });
  }, [refresh, storeId]);

  /** After a step is saved: refresh progress and go to the next screen. */
  const next = useCallback(
    async (from: Screen) => {
      await refresh();
      setScreen(SCREENS[Math.min(SCREENS.indexOf(from) + 1, SCREENS.length - 1)] ?? 'publish');
    },
    [refresh],
  );

  const failed = storeError ?? error;
  if (failed !== null) return <Alert tone="error">{failed}</Alert>;
  if (store === null || state === null || screen === null) return <Loading />;

  const index = SCREENS.indexOf(screen);
  const stepOf = (key: Screen) => state.steps.find((step) => step.key === key);
  return (
    <div className="mrigl-stack">
      <p>
        <Link to={`/stores/${storeId}`}>{display(store.name, locale, store, store.slug)}</Link>
      </p>
      <h1 className="mrigl-heading">{t('admin.setup.title')}</h1>
      <p className="admin-setup__progress">
        {t('admin.setup.progress', { current: index + 1, total: SCREENS.length })}
      </p>
      <div className="admin-setup">
        <nav aria-labelledby={headingId} className="admin-setup__steps">
          <h2 id={headingId} className="mrigl-visually-hidden">
            {t('admin.setup.steps')}
          </h2>
          <ol>
            {SCREENS.map((key) => {
              const step = stepOf(key);
              const done = key === 'publish' ? state.publishedAt !== null : step?.done === true;
              return (
                <li key={key}>
                  <button
                    type="button"
                    className="admin-setup__step"
                    aria-current={key === screen ? 'step' : undefined}
                    onClick={() => {
                      setScreen(key);
                    }}
                  >
                    {done ? (
                      <Check size={16} aria-hidden="true" className="admin-setup__done" />
                    ) : (
                      <Circle size={16} aria-hidden="true" />
                    )}
                    <span>{t(`admin.setup.step.${key}` as MessageKey)}</span>
                    <span className="mrigl-visually-hidden">
                      {done ? t('admin.setup.done') : t('admin.setup.todo')}
                    </span>
                    {step !== undefined && !step.required ? (
                      <span className="admin-status">{t('admin.setup.optional')}</span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>
        <div className="admin-setup__panel">
          {screen === 'plan' ? (
            <PlanStep storeId={storeId} onDone={() => void next('plan')} />
          ) : null}
          {screen === 'contact' ? (
            <ContactStep
              store={store}
              onDone={(saved) => {
                setStore(saved);
                void next('contact');
              }}
            />
          ) : null}
          {screen === 'legal' ? (
            <LegalStep store={store} onDone={() => void next('legal')} />
          ) : null}
          {screen === 'delivery' ? (
            <DeliveryStep store={store} onDone={() => void next('delivery')} />
          ) : null}
          {screen === 'products' ? (
            <ProductsStep
              store={store}
              done={stepOf('products')?.done === true}
              onAdded={() => void refresh()}
              onDone={() => void next('products')}
            />
          ) : null}
          {screen === 'publish' ? (
            <PublishStep store={store} state={state} onChange={setState} onGoTo={setScreen} />
          ) : null}
          {screen !== 'publish' && stepOf(screen)?.required === false ? (
            <Button
              variant="secondary"
              onClick={() => {
                setScreen(SCREENS[index + 1] ?? 'publish');
              }}
            >
              {t('admin.setup.skip')}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function PlanStep({ storeId, onDone }: { readonly storeId: string; readonly onDone: () => void }) {
  const { t, locale, formatPrice } = useI18n();
  const [offered, setOffered] = useState<Schemas['OfferedPlans'] | null>(null);
  const [choice, setChoice] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const name = useId();

  useEffect(() => {
    void unwrap(
      api.GET('/admin-api/v1/stores/{storeId}/billing/plans', { params: { path: { storeId } } }),
    )
      .then((plans) => {
        setOffered(plans);
        setChoice(plans.currentPlanId ?? plans.items[0]?.planId ?? '');
      })
      .catch((failure: unknown) => {
        setError(errorMessage(t, failure));
      });
  }, [storeId, t]);

  async function save(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    if (offered === null) return;
    if (!offered.canChoose || offered.items.length === 0 || choice === offered.currentPlanId) {
      onDone();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await unwrap(
        api.PUT('/admin-api/v1/stores/{storeId}/billing/plan', {
          params: { path: { storeId } },
          body: { planId: choice },
        }),
      );
      onDone();
    } catch (failure) {
      setError(errorMessage(t, failure));
    } finally {
      setBusy(false);
    }
  }

  if (offered === null) return error === null ? <Loading /> : <Alert tone="error">{error}</Alert>;
  return (
    <form
      className="mrigl-stack"
      aria-label={t('admin.setup.step.plan')}
      onSubmit={(e) => void save(e)}
    >
      <h2 className="mrigl-subheading">{t('admin.setup.plan.title')}</h2>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {offered.items.length === 0 ? (
        <p>{t('admin.setup.plan.none')}</p>
      ) : (
        <fieldset className="admin-plans" disabled={!offered.canChoose}>
          <legend className="mrigl-visually-hidden">{t('admin.setup.plan.title')}</legend>
          {offered.items.map((plan) => (
            <label key={plan.planId} className="admin-plan">
              <input
                type="radio"
                name={name}
                value={plan.planId}
                checked={choice === plan.planId}
                onChange={() => {
                  setChoice(plan.planId);
                }}
              />
              <span className="admin-plan__body">
                <strong dir="auto">{planName(plan.name, locale, plan.code)}</strong>
                <span>
                  {plan.price.minor === '0'
                    ? t('admin.setup.plan.free')
                    : t('admin.billing.price', {
                        amount: formatPrice(plan.price.minor),
                        months: plan.periodMonths,
                      })}
                </span>
                {plan.trialDays > 0 ? (
                  <span>{t('admin.setup.plan.trial', { days: plan.trialDays })}</span>
                ) : null}
                <span className="mrigl-muted">
                  {t('admin.setup.plan.limits', {
                    products:
                      plan.maxProducts === null
                        ? t('admin.setup.plan.unlimited')
                        : String(plan.maxProducts),
                    staff:
                      plan.maxStaff === null
                        ? t('admin.setup.plan.unlimited')
                        : String(plan.maxStaff),
                  })}
                </span>
              </span>
            </label>
          ))}
        </fieldset>
      )}
      {offered.canChoose ? null : <p className="mrigl-muted">{t('admin.setup.plan.locked')}</p>}
      <PaymentInstructions text={offered.paymentInstructions} />
      <Button type="submit" busy={busy}>
        {t('admin.setup.continue')}
      </Button>
    </form>
  );
}

function ContactStep({
  store,
  onDone,
}: {
  readonly store: StoreDetails;
  readonly onDone: (store: StoreDetails) => void;
}) {
  const { t, locale } = useI18n();
  const [phone, setPhone] = useState(store.contact.phone ?? '');
  const [email, setEmail] = useState(store.contact.email ?? '');
  const [governorate, setGovernorate] = useState(store.contact.governorate ?? '');
  const [address, setAddress] = useState(store.contact.address ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!isGovernorateCode(governorate)) {
      setError(t('admin.setup.contact.governorateMissing'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      onDone(
        await unwrap(
          api.PUT('/admin-api/v1/stores/{storeId}/contact', {
            params: { path: { storeId: store.storeId } },
            body: {
              phone,
              email: email.trim(),
              governorate,
              address: address.trim() === '' ? null : address.trim(),
            },
          }),
        ),
      );
    } catch (failure) {
      setError(errorMessage(t, failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="mrigl-stack"
      aria-label={t('admin.setup.step.contact')}
      onSubmit={(e) => void save(e)}
    >
      <h2 className="mrigl-subheading">{t('admin.setup.contact.title')}</h2>
      <p className="mrigl-muted">{t('admin.setup.contact.intro')}</p>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      <TextField
        label={t('admin.setup.contact.phone')}
        type="tel"
        autoComplete="tel"
        required
        ltr
        value={phone}
        onChange={(event) => {
          setPhone(event.target.value);
        }}
      />
      <TextField
        label={t('admin.setup.contact.email')}
        type="email"
        autoComplete="email"
        required
        ltr
        value={email}
        onChange={(event) => {
          setEmail(event.target.value);
        }}
      />
      <SelectField
        label={t('admin.setup.contact.governorate')}
        required
        value={governorate}
        onChange={(event) => {
          setGovernorate(event.target.value);
        }}
        options={[
          { value: '', label: t('admin.setup.contact.choose') },
          ...governorateOptions(locale),
        ]}
      />
      <TextAreaField
        label={t('admin.setup.contact.address')}
        dir="auto"
        maxLength={300}
        value={address}
        onChange={(event) => {
          setAddress(event.target.value);
        }}
      />
      <Button type="submit" busy={busy}>
        {t('admin.setup.saveContinue')}
      </Button>
    </form>
  );
}

function LegalStep({
  store,
  onDone,
}: {
  readonly store: StoreDetails;
  readonly onDone: () => void;
}) {
  const { t } = useI18n();
  const language = store.defaultLocale;
  const [current, setCurrent] = useState<Schemas['InvoiceSettingsState'] | null>(null);
  const [legalName, setLegalName] = useState('');
  const [address, setAddress] = useState('');
  const [taxId, setTaxId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void unwrap(
      api.GET('/admin-api/v1/stores/{storeId}/invoice-settings', {
        params: { path: { storeId: store.storeId } },
      }),
    )
      .then((state) => {
        setCurrent(state);
        setLegalName(state.settings.legalName[language] ?? '');
        setAddress(state.settings.address[language] ?? '');
        setTaxId(state.settings.taxId ?? '');
      })
      .catch((failure: unknown) => {
        setError(errorMessage(t, failure));
      });
  }, [store.storeId, language, t]);

  async function save(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    if (current === null) return;
    setBusy(true);
    setError(null);
    try {
      await unwrap(
        api.PUT('/admin-api/v1/stores/{storeId}/invoice-settings', {
          params: { path: { storeId: store.storeId } },
          body: {
            version: Math.max(current.version, 1),
            settings: {
              ...current.settings,
              legalName: { ...current.settings.legalName, [language]: legalName.trim() },
              address: { ...current.settings.address, [language]: address.trim() },
              taxId: taxId.trim() === '' ? null : taxId.trim(),
            },
          },
        }),
      );
      onDone();
    } catch (failure) {
      setError(errorMessage(t, failure));
    } finally {
      setBusy(false);
    }
  }

  if (current === null) return error === null ? <Loading /> : <Alert tone="error">{error}</Alert>;
  const languageName = t(`admin.language.${language}`);
  return (
    <form
      className="mrigl-stack"
      aria-label={t('admin.setup.step.legal')}
      onSubmit={(e) => void save(e)}
    >
      <h2 className="mrigl-subheading">{t('admin.setup.legal.title')}</h2>
      <p className="mrigl-muted">{t('admin.setup.legal.intro')}</p>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      <TextField
        label={t('admin.setup.legal.name', { language: languageName })}
        lang={language}
        dir="auto"
        required
        maxLength={200}
        value={legalName}
        onChange={(event) => {
          setLegalName(event.target.value);
        }}
      />
      <TextAreaField
        label={t('admin.setup.legal.address', { language: languageName })}
        lang={language}
        dir="auto"
        required
        maxLength={500}
        value={address}
        onChange={(event) => {
          setAddress(event.target.value);
        }}
      />
      <TextField
        label={t('admin.setup.legal.taxId')}
        hint={t('admin.setup.legal.taxIdHint')}
        ltr
        maxLength={40}
        value={taxId}
        onChange={(event) => {
          setTaxId(event.target.value);
        }}
      />
      <p>
        <Link to={`/stores/${store.storeId}/invoicing`}>{t('admin.setup.legal.more')}</Link>
      </p>
      <Button type="submit" busy={busy}>
        {t('admin.setup.saveContinue')}
      </Button>
    </form>
  );
}

function DeliveryStep({
  store,
  onDone,
}: {
  readonly store: StoreDetails;
  readonly onDone: () => void;
}) {
  const { t } = useI18n();
  const language = store.defaultLocale;
  const [current, setCurrent] = useState<Schemas['CheckoutSettingsState'] | null>(null);
  const [payments, setPayments] = useState<string[]>([]);
  const [instructions, setInstructions] = useState('');
  const [pickup, setPickup] = useState(false);
  const [pickupAddress, setPickupAddress] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void unwrap(
      api.GET('/admin-api/v1/stores/{storeId}/checkout-settings', {
        params: { path: { storeId: store.storeId } },
      }),
    )
      .then((state) => {
        const { settings } = state;
        setCurrent(state);
        setPayments([
          ...(settings.payments.cashOnDelivery.enabled ? ['cod'] : []),
          ...(settings.payments.bankTransfer.enabled ? ['bank'] : []),
        ]);
        setInstructions(settings.payments.bankTransfer.instructions[language] ?? '');
        setPickup(settings.pickup.enabled);
        setPickupAddress(settings.pickup.address[language] ?? '');
      })
      .catch((failure: unknown) => {
        setError(errorMessage(t, failure));
      });
  }, [store.storeId, language, t]);

  async function save(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    if (current === null) return;
    const { settings } = current;
    const zones = settings.shipping.enabled && settings.shipping.zones.length > 0;
    if (payments.length === 0 || (!pickup && !zones)) {
      setError(t('admin.setup.delivery.incomplete'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await unwrap(
        api.PUT('/admin-api/v1/stores/{storeId}/checkout-settings', {
          params: { path: { storeId: store.storeId } },
          body: {
            version: Math.max(current.version, 1),
            settings: {
              ...settings,
              payments: {
                cashOnDelivery: { enabled: payments.includes('cod') },
                bankTransfer: {
                  enabled: payments.includes('bank'),
                  instructions: {
                    ...settings.payments.bankTransfer.instructions,
                    [language]: instructions.trim(),
                  },
                },
              },
              pickup: {
                enabled: pickup,
                address: { ...settings.pickup.address, [language]: pickupAddress.trim() },
              },
            },
          },
        }),
      );
      onDone();
    } catch (failure) {
      setError(errorMessage(t, failure));
    } finally {
      setBusy(false);
    }
  }

  if (current === null) return error === null ? <Loading /> : <Alert tone="error">{error}</Alert>;
  const zones = current.settings.shipping.enabled ? current.settings.shipping.zones.length : 0;
  return (
    <form
      className="mrigl-stack"
      aria-label={t('admin.setup.step.delivery')}
      onSubmit={(e) => void save(e)}
    >
      <h2 className="mrigl-subheading">{t('admin.setup.delivery.title')}</h2>
      <p className="mrigl-muted">{t('admin.setup.delivery.intro')}</p>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      <CheckboxGroup
        legend={t('admin.checkout.payments')}
        options={[
          { value: 'cod', label: t('admin.checkout.cashOnDelivery') },
          { value: 'bank', label: t('admin.checkout.bankTransfer') },
        ]}
        values={payments}
        onChange={setPayments}
      />
      {payments.includes('bank') ? (
        <TextAreaField
          label={t('admin.setup.delivery.bankInstructions')}
          lang={language}
          dir="auto"
          maxLength={1000}
          value={instructions}
          onChange={(event) => {
            setInstructions(event.target.value);
          }}
        />
      ) : null}
      <Card labelledBy="setup-shipping">
        <h3 id="setup-shipping" className="mrigl-subheading">
          {t('admin.setup.delivery.shipping')}
        </h3>
        <p>{t('admin.setup.delivery.zones', { count: zones })}</p>
        <Link to={`/stores/${store.storeId}/checkout`}>{t('admin.setup.delivery.zonesLink')}</Link>
      </Card>
      <CheckboxGroup
        legend={t('admin.checkout.pickupEnabled')}
        options={[{ value: 'pickup', label: t('admin.checkout.pickupEnabled') }]}
        values={pickup ? ['pickup'] : []}
        onChange={(values) => {
          setPickup(values.includes('pickup'));
        }}
      />
      {pickup ? (
        <TextAreaField
          label={t('admin.setup.delivery.pickupAddress')}
          lang={language}
          dir="auto"
          maxLength={300}
          value={pickupAddress}
          onChange={(event) => {
            setPickupAddress(event.target.value);
          }}
        />
      ) : null}
      <Button type="submit" busy={busy}>
        {t('admin.setup.saveContinue')}
      </Button>
    </form>
  );
}

/**
 * Idempotency-Key for the stock count. The catalog `newKey()` is 7 characters; the API
 * requires 8–100 of letters, digits, `.`, `_`, `:` and `-`.
 */
function idempotencyKey(): string {
  return `setup-${globalThis.crypto.randomUUID()}`;
}

/** Lower-case Latin letters and digits from the title, plus a short random suffix. */
function handleFor(title: string): string {
  const base = title
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return `${base === '' ? 'produit' : base}-${newKey().slice(0, 4).toLowerCase()}`;
}

function ProductsStep({
  store,
  done,
  onAdded,
  onDone,
}: {
  readonly store: StoreDetails;
  readonly done: boolean;
  readonly onAdded: () => void;
  readonly onDone: () => void;
}) {
  const { t } = useI18n();
  const language = store.defaultLocale;
  const [title, setTitle] = useState('');
  const [price, setPrice] = useState('');
  const [stock, setStock] = useState('1');
  const [photo, setPhoto] = useState<File | null>(null);
  const [added, setAdded] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [formKey, setFormKey] = useState(0);

  async function add(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    const minor = toMinor(price);
    if (title.trim() === '' || minor === null || minor === '0') {
      setError(t('admin.setup.products.invalid'));
      return;
    }
    if (!/^[0-9]+$/.test(stock.trim())) {
      setError(t('admin.inventory.error.quantity'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const media = photo === null ? null : await uploadImage(store.storeId, photo);
      const product = await unwrap(
        api.POST('/admin-api/v1/stores/{storeId}/products', {
          params: { path: { storeId: store.storeId } },
          body: {
            status: 'active',
            handle: handleFor(title),
            title: { [language]: title.trim() },
            description: {},
            tags: [],
            taxClass: null,
            options: [],
            variants: [
              {
                optionValues: [],
                sku: null,
                barcode: null,
                price: { minor, currency: 'TND' },
                compareAtPrice: null,
                weightGrams: null,
              },
            ],
            mediaIds: media === null ? [] : [media.mediaId],
          },
        }),
      );
      const variantId = product.variants[0]?.variantId;
      if (variantId !== undefined) {
        const path = { storeId: store.storeId, variantId };
        await unwrap(
          api.PUT('/admin-api/v1/stores/{storeId}/inventory/{variantId}/settings', {
            params: { path },
            body: { tracked: true, allowBackorder: false },
          }),
        );
        await unwrap(
          api.POST('/admin-api/v1/stores/{storeId}/inventory/{variantId}/counts', {
            params: { path, header: { 'idempotency-key': idempotencyKey() } },
            body: { onHand: Number(stock), note: null },
          }),
        );
      }
      setAdded([...added, title.trim()]);
      setTitle('');
      setPrice('');
      setStock('1');
      setPhoto(null);
      setFormKey(formKey + 1);
      onAdded();
    } catch (failure) {
      setError(errorMessage(t, failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mrigl-stack">
      <h2 className="mrigl-subheading">{t('admin.setup.products.title')}</h2>
      <p className="mrigl-muted">{t('admin.setup.products.intro')}</p>
      {done ? <Alert tone="success">{t('admin.setup.products.ready')}</Alert> : null}
      {added.length === 0 ? null : (
        <ul className="mrigl-list" aria-label={t('admin.setup.products.added')}>
          {added.map((name, index) => (
            <li key={`${name}-${String(index)}`} dir="auto">
              {name}
            </li>
          ))}
        </ul>
      )}
      <form
        key={formKey}
        className="mrigl-card mrigl-stack"
        aria-label={t('admin.setup.products.form')}
        onSubmit={(e) => void add(e)}
      >
        {error === null ? null : <Alert tone="error">{error}</Alert>}
        <TextField
          label={t('admin.setup.products.name')}
          lang={language}
          dir="auto"
          required
          maxLength={200}
          value={title}
          onChange={(event) => {
            setTitle(event.target.value);
          }}
        />
        <TextField
          label={t('admin.setup.products.price')}
          inputMode="decimal"
          required
          ltr
          value={price}
          onChange={(event) => {
            setPrice(event.target.value);
          }}
        />
        <TextField
          label={t('admin.setup.products.stock')}
          inputMode="numeric"
          required
          ltr
          value={stock}
          onChange={(event) => {
            setStock(event.target.value);
          }}
        />
        <TextField
          label={t('admin.setup.products.photo')}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/avif"
          onChange={(event) => {
            setPhoto(event.target.files?.[0] ?? null);
          }}
        />
        <Button type="submit" busy={busy}>
          {t('admin.setup.products.add')}
        </Button>
      </form>
      <p>
        <Link to={`/stores/${store.storeId}/products`}>{t('admin.setup.products.more')}</Link>
      </p>
      <Button variant={done ? 'primary' : 'secondary'} disabled={!done} onClick={onDone}>
        {t('admin.setup.continue')}
      </Button>
    </div>
  );
}

function policyHref(configured: string | null, hash: 'terms' | 'privacy'): string | null {
  if (configured !== null && configured.length > 0) return configured;
  if (typeof location !== 'undefined' && location.hostname.endsWith('localhost')) {
    return `http://localhost:3200/#${hash}`;
  }
  return null;
}

function PublishStep({
  store,
  state,
  onChange,
  onGoTo,
}: {
  readonly store: StoreDetails;
  readonly state: Onboarding;
  readonly onChange: (state: Onboarding) => void;
  readonly onGoTo: (screen: Screen) => void;
}) {
  const { t } = useI18n();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(state.policies.accepted);
  const host = store.hosts.find((item) => item.canonical)?.host ?? store.hosts[0]?.host ?? '';
  const termsHref = policyHref(state.policies.termsUrl, 'terms');
  const privacyHref = policyHref(state.policies.privacyUrl, 'privacy');

  async function preview() {
    setError(null);
    try {
      const link = await unwrap(
        api.POST('/admin-api/v1/stores/{storeId}/theme/preview', {
          params: { path: { storeId: store.storeId } },
        }),
      );
      window.open(link.url, '_blank', 'noopener,noreferrer');
    } catch (failure) {
      setError(errorMessage(t, failure));
    }
  }

  async function publish() {
    setBusy(true);
    setError(null);
    try {
      onChange(
        await unwrap(
          api.POST('/admin-api/v1/stores/{storeId}/onboarding/publish', {
            params: { path: { storeId: store.storeId } },
            body: { accepted: true },
          }),
        ),
      );
    } catch (failure) {
      setError(errorMessage(t, failure));
    } finally {
      setBusy(false);
    }
  }

  if (state.publishedAt !== null) {
    return (
      <div className="mrigl-stack">
        <Alert tone="success">{t('admin.setup.publish.online')}</Alert>
        <p>
          <a className="mrigl-ltr" href={`https://${host}`} target="_blank" rel="noreferrer">
            {host}
          </a>
        </p>
        <Link className="mrigl-button mrigl-button--primary" to={`/stores/${store.storeId}`}>
          {t('admin.setup.publish.toStore')}
        </Link>
      </div>
    );
  }
  const missing = state.steps.filter((step) => step.required && !step.done);
  return (
    <div className="mrigl-stack">
      <h2 className="mrigl-subheading">{t('admin.setup.publish.title')}</h2>
      <p className="mrigl-muted">{t('admin.setup.publish.intro')}</p>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      <ul className="admin-setup__checklist">
        {state.steps.map((step) => (
          <li key={step.key}>
            {step.done ? (
              <Check size={16} aria-hidden="true" className="admin-setup__done" />
            ) : (
              <Circle size={16} aria-hidden="true" />
            )}
            <button
              type="button"
              className="admin-linkbutton"
              onClick={() => {
                onGoTo(step.key);
              }}
            >
              {t(`admin.setup.step.${step.key}` as MessageKey)}
            </button>
            <span className="mrigl-muted">
              {step.done
                ? t('admin.setup.done')
                : step.required
                  ? t('admin.setup.required')
                  : t('admin.setup.optional')}
            </span>
          </li>
        ))}
      </ul>
      {missing.length > 0 ? (
        <p className="admin-notice" role="status">
          {t('admin.setup.publish.missing', { count: missing.length })}
        </p>
      ) : null}
      <div className="mrigl-stack">
        <p>{t('admin.setup.publish.policies')}</p>
        <p>
          {termsHref === null ? (
            t('landing.terms')
          ) : (
            <a href={termsHref} target="_blank" rel="noreferrer">
              {t('landing.terms')}
            </a>
          )}
          {' · '}
          {privacyHref === null ? (
            t('landing.privacy')
          ) : (
            <a href={privacyHref} target="_blank" rel="noreferrer">
              {t('landing.privacy')}
            </a>
          )}
        </p>
        <CheckboxGroup
          legend={t('admin.setup.publish.accept')}
          options={[{ value: 'yes', label: t('admin.setup.publish.accept') }]}
          values={accepted ? ['yes'] : []}
          onChange={(values) => {
            setAccepted(values.includes('yes'));
          }}
        />
      </div>
      <div className="admin-actions">
        <Button variant="secondary" onClick={() => void preview()}>
          {t('admin.setup.publish.preview')}
        </Button>
        <Button
          busy={busy}
          disabled={!state.canPublish || !accepted}
          onClick={() => void publish()}
        >
          {t('admin.setup.publish.action')}
        </Button>
      </div>
    </div>
  );
}
